import { Redirect } from '@omnea-app/fe-routing/Redirect'
import { FullWidthLoadingFallback } from '@omnea-app/frontend/components/shared/Loading/components/LoadingFallbacks'
import { CustomErrorBoundary } from '@omnea-app/frontend/utils/error-boundary/CustomErrorBoundary'
import { assertNever } from '@omnea-app/packages/universal/type-guards/assertNever'
import { useFullAuthzContext } from './AuthzContextProvider'
import { RecordAccessGate } from './RecordAccessGate'
import { resolveRouteKey } from './resolveRouteKey'
import { hrefFor } from './routeHref'
import { DEFAULT_DENIED_REDIRECT, ROUTE_REGISTRY, type RouteDefinition } from './routeRegistry'
import { useRecordAccess } from './useRecordAccess'
import { useRouteAccess } from './useRouteAccess'
import type { PermissionSetEntity } from '@omnea-app/authz'
import type { ParsedUrlQuery } from 'querystring'
import type { PropsWithChildren } from 'react'

export type GatedRoute = { pattern: string; query: ParsedUrlQuery; isQueryResolved: boolean }

type RouteAccessGateProps = PropsWithChildren<{ route: GatedRoute }>

/**
 * Withholds a route the caller may not reach. Mounted only for authenticated callers, since
 * `useRouteAccess` reads a context the unauthenticated tree never mounts.
 */
export const RouteAccessGate = ({ children, route }: RouteAccessGateProps) => {
  const access = useRouteAccess()
  const authzContext = useFullAuthzContext()

  // Next leaves the query empty until hydration, and a key discriminated by a query segment would
  // resolve to nothing — rendering the route ungated rather than withheld.
  if (route.isQueryResolved === false) return <FullWidthLoadingFallback />

  const routeKey = resolveRouteKey(route.pattern, route.query)

  // A route the registry does not name stays reachable, so coverage can grow without a missing key
  // becoming a silent outage.
  if (routeKey == null) return <>{children}</>

  const definition: RouteDefinition = ROUTE_REGISTRY[routeKey]
  // Every `RouteKey` is answered, so a miss is impossible; withhold rather than deny if one ever is.
  const answer = access.get(routeKey) ?? 'pending'

  switch (answer) {
    case 'unavailable':
      return <CustomErrorBoundary errorData={{ error: authzContext.status === 'unavailable' ? authzContext.error : undefined }} />
    case 'pending':
      return <FullWidthLoadingFallback />
    case 'allowed':
      if (definition.record == null) return <>{children}</>
      return (
        <RecordRouteGate entity={definition.record} recordId={readRecordId(route.query)}>
          {children}
        </RecordRouteGate>
      )
    case 'denied':
      return <Redirect to={hrefFor(definition.deniedRedirectTo ?? DEFAULT_DENIED_REDIRECT)} type="replace" />
    default:
      return assertNever(answer)
  }
}

const readRecordId = (query: ParsedUrlQuery): string => {
  const id = Array.isArray(query.id) ? query.id[0] : query.id
  if (id == null) throw new Error('Record route has no id in its query')
  return id
}

type RecordRouteGateProps = PropsWithChildren<{ entity: PermissionSetEntity; recordId: string }>

// Its own component so the record hook runs only for routes that declare a record.
const RecordRouteGate = ({ entity, recordId, children }: RecordRouteGateProps) => {
  const access = useRecordAccess(entity, recordId)
  return <RecordAccessGate access={access}>{children}</RecordAccessGate>
}
